import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { isApiClientError } from "../api/errors";
import type { DocumentWorkflowFileVersion } from "../entities/document-workflow";
import { uploadConfirmedFile } from "../entities/files";
import type { ShellServices } from "./services";

/** Код Core: версия, на которой основана новая, уже не актуальна. */
const BASE_VERSION_CONFLICT = "document_workflow.immutable_version";

/** Загруженный и подтверждённый файл, ещё не ставший версией. */
export interface PendingVersion {
  readonly confirmedFileId: string;
  readonly fileName: string;
  /** Версия, которую человек видел, начиная операцию. */
  readonly baseVersionId: string;
}

export type VersionUploadState =
  | { readonly phase: "idle" }
  | { readonly phase: "uploading"; readonly progress: number | null }
  | {
      readonly phase: "created";
      readonly version: DocumentWorkflowFileVersion;
    }
  /**
   * Параллельное изменение: пока шла загрузка, у файла появилась другая
   * версия. Это не сетевая ошибка — новая версия намеренно не создана, чтобы
   * не затереть чужую работу.
   */
  | {
      readonly phase: "conflict";
      readonly pending: PendingVersion;
      /** Актуальная версия после обновления истории. */
      readonly latest: DocumentWorkflowFileVersion | null;
      /** История уже перечитана: `latest` — не догадка, а ответ сервера. */
      readonly refreshed: boolean;
      readonly refreshing: boolean;
      readonly refreshFailed: boolean;
    }
  | { readonly phase: "failed"; readonly message: string };

/** Объяснение неудачи загрузки или создания версии, кроме конфликта. */
export function versionUploadMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.code === "scope.not_selected") {
      return "Выберите организацию в шапке: файл должен кому-то принадлежать.";
    }
    if (error.status === 403) {
      return "Недостаточно прав для загрузки новой версии этого файла.";
    }
    if (error.status === 404) {
      return "Файл не найден или больше не доступен. Обновите карточку.";
    }
    // Конфликт без кода версии-основы — не параллельное изменение: чаще всего
    // организация в шапке не выбрана.
    if (error.status === 409) {
      return "Операция не выполнена: выберите организацию в шапке или обновите карточку и повторите попытку.";
    }
    if (error.status === 413) return "Файл больше, чем принимает хранилище.";
    if (error.status === 422) {
      return "Файл не принят: он исполняемый, повреждён либо не подтверждён. Повторите загрузку.";
    }
    if (error.status === 503) {
      return "Хранилище временно недоступно. Повторите попытку позже.";
    }
  }

  return "Не удалось загрузить новую версию. Проверьте подключение и повторите попытку.";
}

function isBaseVersionConflict(error: unknown): boolean {
  return isApiClientError(error) && error.code === BASE_VERSION_CONFLICT;
}

/**
 * Загрузка новой версии логического файла.
 *
 * Байты уходят общим путём «подписанная ссылка → хранилище → подтверждение»,
 * а в workflow передаётся только идентификатор подтверждённого файла и
 * `base_version_id` — версия, которую человек видел в начале операции.
 */
export function useNewVersionUpload({
  services,
  fileId,
  accessScopeId,
}: {
  services: ShellServices;
  fileId: string;
  accessScopeId?: string;
}) {
  const client = useQueryClient();
  const [state, setState] = useState<VersionUploadState>({ phase: "idle" });
  // Версия, выбранная человеком как основа, запоминается в начале операции, а
  // не при отправке: список за это время мог обновиться в фоне.
  const base = useRef<string | null>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const refreshLists = useCallback(
    () => client.invalidateQueries({ queryKey: ["document-workflow"] }),
    [client],
  );

  const create = useCallback(
    async (pending: PendingVersion) => {
      try {
        const version = await services.documentWorkflow.createFileVersion(
          fileId,
          {
            confirmed_file_id: pending.confirmedFileId,
            base_version_id: pending.baseVersionId,
          },
        );
        if (!mounted.current) return;
        setState({ phase: "created", version });
        await refreshLists();
      } catch (error) {
        if (!mounted.current) return;
        if (isBaseVersionConflict(error)) {
          setState({
            phase: "conflict",
            pending,
            latest: null,
            refreshed: false,
            refreshing: false,
            refreshFailed: false,
          });
          return;
        }
        setState({ phase: "failed", message: versionUploadMessage(error) });
      }
    },
    [fileId, refreshLists, services.documentWorkflow],
  );

  /** Человек начал операцию: запомнить версию, которую он видит. */
  const begin = useCallback((baseVersionId: string) => {
    base.current = baseVersionId;
  }, []);

  const upload = useCallback(
    async (file: File, displayedVersionId: string) => {
      // Обычно основа запомнена нажатием кнопки. Если файл выбран мимо неё —
      // например, с клавиатуры прямо в поле выбора, — основой служит версия,
      // показанная в этот момент.
      const baseVersionId = base.current ?? displayedVersionId;
      base.current = null;
      setState({ phase: "uploading", progress: null });
      let confirmed;
      try {
        confirmed = await uploadConfirmedFile(services.files, file, {
          accessScopeId,
          onProgress: (progress) => {
            if (mounted.current) setState({ phase: "uploading", progress });
          },
        });
      } catch (error) {
        if (mounted.current) {
          setState({ phase: "failed", message: versionUploadMessage(error) });
        }
        return;
      }
      await create({
        confirmedFileId: confirmed.id,
        fileName: file.name,
        baseVersionId,
      });
    },
    [accessScopeId, create, services.files],
  );

  /** Перечитать файл и историю, чтобы увидеть, что изменилось. */
  const refreshHistory = useCallback(async () => {
    setState((current) =>
      current.phase === "conflict"
        ? { ...current, refreshing: true, refreshFailed: false }
        : current,
    );
    try {
      const latest = await services.documentWorkflow.documentFile(fileId);
      await refreshLists();
      if (!mounted.current) return;
      setState((current) =>
        current.phase === "conflict"
          ? {
              ...current,
              latest: latest.current_version,
              refreshed: true,
              refreshing: false,
            }
          : current,
      );
    } catch {
      if (!mounted.current) return;
      setState((current) =>
        current.phase === "conflict"
          ? { ...current, refreshing: false, refreshFailed: true }
          : current,
      );
    }
  }, [fileId, refreshLists, services.documentWorkflow]);

  /**
   * Повторить с уже загруженным файлом поверх актуальной версии.
   *
   * Доступно только после обновления истории: человек сначала видит, что
   * изменилось, и лишь потом решает, создавать ли версию поверх.
   */
  const retryOnLatest = useCallback(async () => {
    if (
      state.phase !== "conflict" ||
      !state.refreshed ||
      state.latest === null
    ) {
      return;
    }
    setState({ phase: "uploading", progress: 1 });
    await create({ ...state.pending, baseVersionId: state.latest.id });
  }, [create, state]);

  const dismiss = useCallback(() => {
    base.current = null;
    setState({ phase: "idle" });
  }, []);

  return { state, begin, upload, refreshHistory, retryOnLatest, dismiss };
}
